import * as RA from 'ramda-adjunct'

export type NEList<T> = [T, ...T[]]
export type NEListInitializer<T> = T | NEList<T>
export type NEListUnsafeInitializer<T> = NEListInitializer<T> | T[]

/** Narrows T[] to NEList<T> if it passes a runtime check for length. */
export const isNEList = <T>(array: T[]): array is NEList<T> => array.length > 0

/** Get a NEList back from one or many items */
export const create = <const T>(items: NEListInitializer<T>): NEList<T> => {
  return RA.ensureArray(items) as NEList<T>
}

/**
 * Get a NEList back from one, many, or a list of items.
 * This is `unsafe` because there are no built-in guarantees that the list is not empty
 */
export const createUnsafe = <T>(items: NEListUnsafeInitializer<T>): NEList<T> => {
  return create(items as NEList<T>)
}

/**
 * Get a NEList back from one, many, or a list of items.
 * This is `safe` because it will return `undefined` if the given list is empty
 */
export const createSafe = <T>(
  items: NEListUnsafeInitializer<T>
): NEList<T> | undefined => {
  const l = createUnsafe(items)
  return l.length ? l : undefined
}

/** Get the member of the NEList at index i. Returns the last element if i overflows the size of the list (or if it is negative). */
export const atOrLast = <T>(i: number, list: NEList<T>): T => {
  const size = list.length
  return i >= size || i < 0 ? list[size - 1]! : list[i]!
}

/** Get the first element of the list. */
export const head = <T>(list: NEList<T>): T => list[0]

/**
 * Get all but the first element of the list.
 * Returns a native Array because the result may be empty.
 */
export const tail = <T>(list: NEList<T>): T[] => list.slice(1)

/** Like tail, but will return a NEList when possible, or undefined if the input has only one element */
export const tailNE = <T>(list: NEList<T>): NEList<T> | undefined =>
  createSafe(tail(list))

/** Get the last element of the list. (i.e. `(head . reverse)(list)`) */
export const last = <T>(list: NEList<T>): T => list[list.length - 1]!

/**
 * Get all but the last element of the list. (i.e. `(reverse . tail . reverse)(list)`
 * Returns a native Array because the result may be empty.
 */
export const notLast = <T>(list: NEList<T>): T[] => list.slice(0, list.length - 1)

/** Like notLast, but will return a NEList when possible, or undefined if the input has only one element */
export const notLastNE = <T>(list: NEList<T>): NEList<T> | undefined =>
  createSafe(notLast(list))

/**
 * Runs the NEList's values through a `Set` to produce a perhaps-shorter NEList of unique values.
 * (Since the input list is non-empty, the output list is guaranteed to be, also).
 */
export const uniq = <T>(list: NEList<T>): NEList<T> => {
  return createUnsafe(Array.from(new Set(list).values()))
}

/** Returns a new NEList, where each item is generated by the given function from the list's values. */
export const map = <T, U>(
  func: (value: T, i: number, values: NEList<T>) => U,
  list: NEList<T>
): NEList<U> => {
  return createUnsafe(list.map((x, i) => func(x, i, list)))
}

/** Returns a new NEList by adding a new item (or NEList of new items) to an existing NEList. */
export function concat<T>(list: NEList<T>, item: NEListInitializer<T>): NEList<T>
export function concat<T, U>(
  list: NEList<T>,
  item: NEListInitializer<U>
): NEList<T | U>
export function concat<T, U>(
  list: NEList<T>,
  item: NEListInitializer<T>
): NEList<T | U> {
  return create([...list, ...create(item)])
}
